import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "../baseQuery";
import {
  AllReportsParams,
  CreateReport,
  CreateReportSchedules,
  ExecuteReport,
  GetReportSchedules,
  ReportItem,
  ReportsResponse,
  UpdateReportItem,
} from "./models";

export const reportsApi = createApi({
  baseQuery,
  reducerPath: "reportsApi",
  tagTypes: ["reports", "report-categories"],
  endpoints: (builder) => ({
    getReports: builder.query<ReportsResponse, AllReportsParams>({
      query: (params) => ({
        url: "/api/v1/reports/",
        method: "GET",
        params,
      }),
      providesTags: ["reports"],
    }),

    getAllReports: builder.query<ReportItem[], AllReportsParams>({
      query: (params) => ({
        url: "/api/v1/reports/",
        method: "GET",
        params,
      }),
      providesTags: ["reports"],
    }),

    deleteReport: builder.mutation<void, ReportItem>({
      query: (body) => ({
        url: `/api/v1/reports/${body.id}/`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["reports"],
    }),

    updateReport: builder.mutation<void, UpdateReportItem>({
      query: (body) => ({
        url: `/api/v1/reports/${body.id}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["reports"],
    }),

    createReport: builder.mutation<void, CreateReport>({
      query: (body) => ({
        url: `/api/v1/reports/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["reports"],
    }),

    executeReport: builder.mutation<Blob, ExecuteReport>({
      query: (body) => ({
        url: `/api/v1/reports/${body.id}/execute_report/`,
        method: "POST",
        body,

        responseHandler: (response) => response.blob(),
      }),
      invalidatesTags: ["reports"],
    }),

    getReportData: builder.mutation<void, ExecuteReport>({
      query: (body) => ({
        url: `/api/v1/reports/${body.id}/get_report_data/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["reports"],
    }),

    getReportCategories: builder.query<
      { id: number; name: string; order: number }[],
      AllReportsParams
    >({
      query: (params) => ({
        url: `/api/v1/report_categories/`,
        method: "GET",
        params,
      }),
      providesTags: ["report-categories"],
    }),

    createReportCategories: builder.mutation<void, { name: string }>({
      query: (body) => ({
        url: `/api/v1/report_categories/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["report-categories"],
    }),

    updateReportCategories: builder.mutation<
      void,
      { id: number; name: string; order?: number }
    >({
      query: (body) => ({
        url: `/api/v1/report_categories/${body.id}/`,
        method: "PUT",
        body: { name: body.name, order: body.order },
      }),
      invalidatesTags: ["report-categories"],
    }),

    deleteReportCategories: builder.mutation<void, { id: number }>({
      query: (body) => ({
        url: `/api/v1/report_categories/${body.id}/`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["report-categories"],
    }),

    // NEW REPORTS & RECIPIENT API`S
    getReportsSchedules: builder.query<GetReportSchedules[], AllReportsParams>({
      query: (params) => ({
        url: "/api/v1/report_schedules/",
        method: "GET",
        params,
      }),
      providesTags: ["reports"],
    }),
    createReportSchedules: builder.mutation<void, CreateReportSchedules>({
      query: (body) => ({
        url: "/api/v1/report_schedules/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["reports"],
    }),
    getReportsSchedulesById: builder.query<GetReportSchedules, { id: number }>({
      query: ({ id }) => ({
        url: `/api/v1/report_schedules/${id}`,
        method: "GET",
      }),
      providesTags: ["reports"],
    }),
    putReportSchedules: builder.mutation<
      GetReportSchedules,
      { id: number; data: CreateReportSchedules }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/report_schedules/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["reports"],
    }),
    patchReportSchedules: builder.mutation<
      GetReportSchedules,
      { id: number; data: CreateReportSchedules }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/report_schedules/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["reports"],
    }),
    deleteReportSchedules: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/api/v1/report_schedules/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["reports"],
    }),
  }),
});

export const {
  useGetReportsQuery,
  useGetAllReportsQuery,
  useDeleteReportMutation,
  useUpdateReportMutation,
  useCreateReportMutation,
  useExecuteReportMutation,
  useGetReportDataMutation,
  useGetReportCategoriesQuery,
  useCreateReportCategoriesMutation,
  useUpdateReportCategoriesMutation,
  useDeleteReportCategoriesMutation,
  // NEW ENDPOINTS
  useCreateReportSchedulesMutation,
  useDeleteReportSchedulesMutation,
  useGetReportsSchedulesByIdQuery,
  useGetReportsSchedulesQuery,
  usePatchReportSchedulesMutation,
  usePutReportSchedulesMutation,
} = reportsApi;
